import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExchangeAlt, faArrowLeft, faCheckCircle, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import i18n, { intlLocale } from '../i18n/i18n';
import { translateServerMessage } from '../i18n/serverMessages';
import './TransferTicketPage.css';

// For the data-loading effects: the hook's `t` changes with the language, and as an effect
// dependency it would reload the events and tickets each time the language is switched.
const tr = (key, options) => i18n.t(key, { ns: 'transfer', ...options });

// The browser's own errors (fetch failing while offline, a reply that isn't JSON) are in English
// whatever language the page is in. English shows them as they are; Spanish shows its own text.
// Messages we threw ourselves, already translated, pass through.
const errorDetail = (err) => {
  const message = (err && err.message) || '';
  if (err instanceof TypeError && /fetch|network|load failed/i.test(message)) {
    return tr('errors.connection', { message });
  }
  if (err instanceof SyntaxError) {
    return tr('errors.unexpectedResponse', { message });
  }
  return message;
};

const emptySearch = { query: '', results: [], loading: false, showDropdown: false, selectedName: null };

/**
 * IBO number or name search, as on the Content Assign page: search with the button or Enter and
 * pick from the dropdown. A bare IBO number can also be typed: it is used as is when the field
 * loses focus, for IBOs who hold tickets but have no account here.
 */
const IboSearchField = ({ id, label, placeholder, search, onQueryChange, onSearch, onSelect, onUseTyped, onShowDropdown }) => {
  const { t } = useTranslation('transfer');
  // The blur handler runs after a dropdown pick has re-rendered the field, so it reads the text
  // through a ref: a pick must not be overwritten by the partial number typed before searching.
  const queryRef = useRef(search.query);
  queryRef.current = search.query;

  return (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <div className="ibo-search-row">
        <div className="autocomplete-container">
          <input
            type="text"
            id={id}
            value={search.query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onSearch(search.query);
              }
            }}
            onFocus={() => { if (search.results.length > 0) onShowDropdown(true); }}
            onBlur={() => setTimeout(() => { onShowDropdown(false); onUseTyped(queryRef.current); }, 200)}
            placeholder={placeholder}
            autoComplete="off"
          />
          {search.loading && <span className="search-spinner"></span>}
          {search.showDropdown && search.results.length > 0 && (
            <div className="autocomplete-dropdown">
              {search.results.map((user, idx) => (
                <div key={idx} className="autocomplete-item" onMouseDown={() => onSelect(user)}>
                  <span className="autocomplete-ibo">{user.iboNumber}</span>
                  <span className="autocomplete-name">{user.iboName || t('notAvailable')}</span>
                  {user.teamName && <span className="autocomplete-team">{user.teamName}</span>}
                </div>
              ))}
            </div>
          )}
          {search.showDropdown && !search.loading && search.results.length === 0 && search.searched && (
            <div className="autocomplete-dropdown">
              <div className="autocomplete-empty">{t('search.noResults')}</div>
            </div>
          )}
        </div>
        <button
          type="button"
          className="ibo-search-btn"
          aria-label={t('search.button', { label })}
          onClick={() => onSearch(search.query)}
          disabled={!search.query || search.query.length < 2}
        >
          <FontAwesomeIcon icon={faSearch} />
        </button>
      </div>
    </div>
  );
};

const TransferTicketPage = () => {
  const { t } = useTranslation('transfer');
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [transferResult, setTransferResult] = useState(null);
  const [events, setEvents] = useState([]);
  const [fromSearch, setFromSearch] = useState(emptySearch);
  const [toSearch, setToSearch] = useState(emptySearch);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketsError, setTicketsError] = useState(null);
  const [formData, setFormData] = useState({
    fromIboNumber: '',
    toIboNumber: '',
    eventId: '',
    ticketId: '',
    numberOfTickets: 1,
    transferReason: ''
  });

  // Fetch available events for the dropdown
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error(tr('errors.noToken'));
        }

        const response = await fetch(`${config.API_BASE_URL}/em/v1/events/upcoming`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error(tr('errors.fetchEventsStatus', { status: response.status }));
        }

        const result = await response.json();

        if (result.status === 'success' && result.data) {
          setEvents(result.data);

          // Auto-select the first event if available
          if (result.data.length > 0) {
            setFormData(prev => ({
              ...prev,
              eventId: result.data[0].id
            }));
          }
        } else {
          throw new Error(translateServerMessage(result.message, tr('errors.loadEventsFallback')));
        }
      } catch (err) {
        console.error('Error fetching events:', err);
        setError(tr('errors.loadEvents', { detail: errorDetail(err) }));
      }
    };

    fetchEvents();
  }, [getAccessToken]);

  // The source IBO's transferable tickets for the selected event: active ones with tickets left,
  // each ticket on its own. Reloaded whenever the source IBO or the event changes.
  useEffect(() => {
    setTickets([]);
    setTicketsError(null);
    setFormData(prev => ({ ...prev, ticketId: '' }));
    if (!formData.fromIboNumber || !formData.eventId) {
      return undefined;
    }

    let cancelled = false;
    const fetchTickets = async () => {
      setTicketsLoading(true);
      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error(tr('errors.noToken'));
        }
        const response = await fetch(
          `${config.API_BASE_URL}/em/v1/admin/tickets/transfer/tickets?iboNumber=${encodeURIComponent(formData.fromIboNumber)}&eventId=${encodeURIComponent(formData.eventId)}`,
          { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
        );
        const result = await response.json();
        if (!response.ok || result.status !== 'success') {
          throw new Error(translateServerMessage(result.message, tr('errors.requestFailed', { status: response.status })));
        }
        if (cancelled) return;
        const list = result.data || [];
        setTickets(list);
        // Nothing to choose between when there is only one.
        if (list.length === 1) {
          setFormData(prev => ({ ...prev, ticketId: String(list[0].ticketId) }));
        }
      } catch (err) {
        console.error('Error fetching tickets:', err);
        if (!cancelled) setTicketsError(tr('errors.loadTickets', { detail: errorDetail(err) }));
      } finally {
        if (!cancelled) setTicketsLoading(false);
      }
    };
    fetchTickets();
    return () => { cancelled = true; };
  }, [formData.fromIboNumber, formData.eventId, getAccessToken]);

  const searchUsers = useCallback(async (query, setSearch) => {
    if (!query || query.trim().length < 2) {
      setSearch(prev => ({ ...prev, results: [], showDropdown: false, loading: false }));
      return;
    }
    setSearch(prev => ({ ...prev, loading: true }));
    try {
      const token = await getAccessToken();
      if (!token) return;
      const response = await fetch(
        `${config.API_BASE_URL}/em/v1/admin/tickets/transfer/users?search=${encodeURIComponent(query.trim())}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
      );
      const result = await response.json();
      if (response.ok && result.status === 'success') {
        setSearch(prev => ({ ...prev, results: result.data || [], showDropdown: true, loading: false, searched: true }));
      } else {
        setSearch(prev => ({ ...prev, results: [], showDropdown: false, loading: false }));
      }
    } catch (err) {
      console.error('Error searching users:', err);
      setSearch(prev => ({ ...prev, results: [], showDropdown: false, loading: false }));
    }
  }, [getAccessToken]);

  // Field-specific handlers for the two IBO searches
  const searchHandlers = (field, search, setSearch) => ({
    search,
    onQueryChange: (value) => {
      setSearch(prev => ({ ...prev, query: value, selectedName: null, searched: false }));
      setFormData(prev => ({ ...prev, [field]: '' }));
    },
    onSearch: (query) => searchUsers(query, setSearch),
    onSelect: (user) => {
      setSearch({ ...emptySearch, query: `${user.iboNumber} - ${user.iboName || ''}`, selectedName: user.iboName || null });
      setFormData(prev => ({ ...prev, [field]: user.iboNumber }));
    },
    onUseTyped: (query) => {
      const typed = (query || '').trim();
      if (/^\d+$/.test(typed)) {
        setFormData(prev => (prev[field] === typed ? prev : { ...prev, [field]: typed }));
      }
    },
    onShowDropdown: (show) => setSearch(prev => ({ ...prev, showDropdown: show })),
  });

  const handleBack = () => {
    navigate('/');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const selectedTicket = tickets.find(t => String(t.ticketId) === String(formData.ticketId)) || null;

  const validateForm = () => {
    const errors = {};

    if (!formData.fromIboNumber.trim()) {
      errors.fromIboNumber = t('validation.fromRequired');
    } else if (!/^\d+$/.test(formData.fromIboNumber)) {
      errors.fromIboNumber = t('validation.digitsOnly');
    }

    if (!formData.toIboNumber.trim()) {
      errors.toIboNumber = t('validation.toRequired');
    } else if (!/^\d+$/.test(formData.toIboNumber)) {
      errors.toIboNumber = t('validation.digitsOnly');
    }

    if (formData.fromIboNumber === formData.toIboNumber) {
      errors.toIboNumber = t('validation.sameIbo');
    }

    if (!formData.eventId) {
      errors.eventId = t('validation.eventRequired');
    }

    if (!formData.ticketId) {
      errors.ticketId = t('validation.ticketRequired');
    }

    if (!formData.numberOfTickets || formData.numberOfTickets < 1) {
      errors.numberOfTickets = t('validation.minTickets');
    } else if (selectedTicket && parseInt(formData.numberOfTickets, 10) > selectedTicket.noOfTickets) {
      errors.numberOfTickets = t('validation.tooManyTickets', { count: selectedTicket.noOfTickets });
    }

    // Transfer reason is now optional, so no validation needed

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Reset error state
    setError(null);

    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      const errorMessage = Object.values(errors).join(t('validation.separator'));
      setError(errorMessage);
      return;
    }

    // Prepare data for submission
    const transferData = {
      fromIboNumber: formData.fromIboNumber,
      toIboNumber: formData.toIboNumber,
      eventId: parseInt(formData.eventId, 10),
      ticketId: parseInt(formData.ticketId, 10),
      numberOfTickets: parseInt(formData.numberOfTickets, 10),
      transferReason: formData.transferReason // Transfer reason is optional
    };

    try {
      setLoading(true);

      const token = await getAccessToken();
      if (!token) {
        throw new Error(t('errors.noToken'));
      }

      const response = await fetch(`${config.API_BASE_URL}/em/v1/admin/tickets/transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(transferData)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(translateServerMessage(result.message, t('errors.requestFailed', { status: response.status })));
      }

      // Show success UI
      setSuccess(true);
      setTransferResult(result.data);

    } catch (err) {
      console.error('Error transferring tickets:', err);
      setError(t('errors.transferFailed', { detail: errorDetail(err) }));
    } finally {
      setLoading(false);
    }
  };

  const handleNewTransfer = () => {
    setSuccess(false);
    setTransferResult(null);
    setFromSearch(emptySearch);
    setToSearch(emptySearch);
    setFormData({
      fromIboNumber: '',
      toIboNumber: '',
      eventId: events.length > 0 ? events[0].id : '',
      ticketId: '',
      numberOfTickets: 1,
      transferReason: ''
    });
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable');
    // Handle the new date format: "2025-05-15 14:30:00 GMT-07:00"
    return new Date(dateString).toLocaleString(intlLocale());
  };

  // The API's type (VIP, EARLY_BIRD…) in the admin's language; in English, as the page always
  // showed it ("Vip"), and a type the page doesn't know the same way.
  const formatTicketType = (type) => (type
    ? t(`ticketTypes.${type}`, { defaultValue: type.charAt(0) + type.slice(1).toLowerCase() })
    : t('ticketOption.defaultType'));

  const ticketLabel = (ticket) => {
    const values = {
      id: ticket.ticketId,
      type: formatTicketType(ticket.ticketType),
      left: t('ticketOption.left', { count: ticket.noOfTickets }),
    };
    return ticket.purchaseDate
      ? t('ticketOption.labelWithDate', { ...values, date: new Date(ticket.purchaseDate).toLocaleDateString(intlLocale()) })
      : t('ticketOption.label', values);
  };

  const withName = (iboNumber, name) => (name ? `${iboNumber} (${name})` : iboNumber);

  return (
    <div className="transfer-ticket-container">
      <h2>{t('title')}</h2>

      <button onClick={handleBack} className="back-button">
        <FontAwesomeIcon icon={faArrowLeft} /> {t('backToDashboard')}
      </button>

      {error && <div className="error-message">{error}</div>}

      {success && transferResult ? (
        <div className="transfer-success">
          <div className="success-icon">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <h3>{t('success.title')}</h3>

          <div className="transfer-details">
            <p><strong>{t('success.fromIbo')}</strong> {withName(transferResult.fromIboNumber, transferResult.fromIboName)}</p>
            <p><strong>{t('success.toIbo')}</strong> {withName(transferResult.toIboNumber, transferResult.toIboName)}</p>
            <p><strong>{t('success.event')}</strong> {transferResult.eventName}</p>
            {transferResult.eventDate && (
              <p><strong>{t('success.eventDate')}</strong> {formatDate(transferResult.eventDate)}</p>
            )}
            {transferResult.eventLocation && (
              <p><strong>{t('success.eventLocation')}</strong> {transferResult.eventLocation}</p>
            )}
            {transferResult.ticketType && (
              <p><strong>{t('success.ticketType')}</strong> {formatTicketType(transferResult.ticketType)}</p>
            )}
            <p><strong>{t('success.transferred')}</strong> {transferResult.transferredTickets}</p>
            <p><strong>{t('success.remainingSource')}</strong> {transferResult.remainingTicketsSource}</p>
            <p><strong>{t('success.totalDestination')}</strong> {transferResult.totalTicketsDestination}</p>
            {transferResult.sourceTicketId && (
              <p><strong>{t('success.sourceTicketId')}</strong> {transferResult.sourceTicketId}</p>
            )}
            {transferResult.destinationTicketId && (
              <p><strong>{t('success.destinationTicketId')}</strong> {transferResult.destinationTicketId}</p>
            )}
            {transferResult.transferReason && (
              <p><strong>{t('success.reason')}</strong> {transferResult.transferReason}</p>
            )}
            {transferResult.transferredBy && (
              <p><strong>{t('success.transferredBy')}</strong> {transferResult.transferredBy}</p>
            )}
            <p><strong>{t('success.transferDate')}</strong> {formatDate(transferResult.transferDate)}</p>
            <p><strong>{t('success.transferId')}</strong> {transferResult.transferId}</p>
          </div>

          <button onClick={handleNewTransfer} className="new-transfer-button">
            <FontAwesomeIcon icon={faExchangeAlt} /> {t('success.newTransfer')}
          </button>
        </div>
      ) : (
        <div className="transfer-form-container">
          <h3>{t('form.title')}</h3>

          <form onSubmit={handleSubmit} className="transfer-form" aria-label={t('form.ariaLabel')}>
            <div className="form-row">
              <IboSearchField
                id="fromIboNumber"
                label={t('form.fromIbo')}
                placeholder={t('form.fromIboPlaceholder')}
                {...searchHandlers('fromIboNumber', fromSearch, setFromSearch)}
              />

              <IboSearchField
                id="toIboNumber"
                label={t('form.toIbo')}
                placeholder={t('form.toIboPlaceholder')}
                {...searchHandlers('toIboNumber', toSearch, setToSearch)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="eventId">{t('form.event')}</label>
              <select
                id="eventId"
                name="eventId"
                value={formData.eventId}
                onChange={handleInputChange}
                required
              >
                <option value="">{t('form.eventPlaceholder')}</option>
                {events.map(event => (
                  <option key={event.id} value={event.id}>
                    {event.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="ticketId">{t('form.ticket')}</label>
              <select
                id="ticketId"
                name="ticketId"
                value={formData.ticketId}
                onChange={handleInputChange}
                disabled={!formData.fromIboNumber || ticketsLoading || tickets.length === 0}
                required
              >
                <option value="">
                  {!formData.fromIboNumber
                    ? t('form.ticketSourceFirst')
                    : ticketsLoading
                      ? t('form.ticketsLoading')
                      : tickets.length === 0
                        ? t('form.noActiveTickets')
                        : t('form.ticketPlaceholder')}
                </option>
                {tickets.map(ticket => (
                  <option key={ticket.ticketId} value={ticket.ticketId}>
                    {ticketLabel(ticket)}
                  </option>
                ))}
              </select>
              {ticketsError && <p className="field-error">{ticketsError}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="numberOfTickets">{t('form.numberOfTickets')}</label>
              <input
                type="number"
                id="numberOfTickets"
                name="numberOfTickets"
                min="1"
                max={selectedTicket ? selectedTicket.noOfTickets : undefined}
                value={formData.numberOfTickets}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="transferReason">{t('form.reason')}</label>
              <textarea
                id="transferReason"
                name="transferReason"
                rows="3"
                value={formData.transferReason}
                onChange={handleInputChange}
                placeholder={t('form.reasonPlaceholder')}
              />
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="submit-button"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faExchangeAlt} />
                {loading ? t('form.processing') : t('form.submit')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default TransferTicketPage;
